import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Button } from '@actual-app/components/button';
import { SvgLogo } from '@actual-app/components/icons/logo';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import { closeBudget } from '#budgetfiles/budgetfilesSlice';
import { Link } from '#components/common/Link';
import { AjustesHuella } from '#components/cuenta/AjustesHuella';
import { puedeUsarHuella } from '#components/cuenta/huella';
import { NuevaLlaveRepuesto } from '#components/cuenta/NuevaLlaveRepuesto';
import { resumenAvisos } from '#components/mobile/avisos/avisos';
import { useAvisos } from '#components/mobile/avisos/useAvisos';
import { CopiaSettings } from '#components/mobile/copia/CopiaSettings';
import { NominaSettings } from '#components/mobile/nomina/NominaSettings';
import { resumenNomina } from '#components/mobile/nomina/textos';
import { useConfigNomina } from '#components/mobile/nomina/useNomina';
import { nombreTrato, useTrato } from '#components/mobile/travesia/trato';
import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import {
  color,
  movimiento,
  radio,
  suave,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { useCompartir } from '#components/pareja/useCompartir';
import { useFeatureFlag } from '#hooks/useFeatureFlag';
import { useGlobalPref } from '#hooks/useGlobalPref';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { usePlan } from '#hooks/usePlan';
import { NOMBRES_IDIOMAS } from '#i18n';
import { solicitarListaDePresupuestos } from '#presupuesto';
import { useDispatch, useSelector } from '#redux';
import { getThemeOptions, useTheme } from '#style/theme';
import { signOut } from '#users/usersSlice';

import { Actualizaciones } from './Actualizaciones';
import { AlgoNoVaSettings } from './AlgoNoVaSettings';
import { AmarreSettings } from './AmarreSettings';
import { BorrarCuentaSettings } from './BorrarCuentaSettings';
import { CompartirSettings } from './CompartirSettings';
import { CurrencySettings } from './Currency';
import { EnableBankingSettings } from './EnableBankingSettings';
import { EncuestaSettings } from './EncuestaSettings';
import { escalaElegida, EscalaSettings, nombreEscala } from './EscalaSettings';
import { ExportBudget } from './Export';
import { FormatSettings } from './Format';
import { LanguageSettings } from './LanguageSettings';
import { BotonLicencias } from './LicenciasSettings';
import { NotificacionesSettings } from './NotificacionesSettings';
import { nombrePlan, PlanSettings } from './PlanSettings';
import { RepairTransactions } from './RepairTransactions';
import { ResetCache } from './Reset';
import { ThemeSettings } from './Themes';
import { TratoSettings } from './TratoSettings';
import { TravesiaSettings } from './TravesiaSettings';
import { UsoSettings } from './UsoSettings';
import { versionRumbo } from './versionRumbo';

/**
 * Ajustes de Rumbo: la cabecera del presupuesto y una lista agrupada
 * (Presupuesto, Tus datos, Apariencia, Avanzado). Cada fila se despliega en
 * su sitio con el apartado de siempre, así nada cambia en cómo se guarda.
 * Fuera lo heredado que no aplica: cambiar de servidor, cifrado,
 * restablecer sincronización y funciones experimentales.
 */
export function AjustesRumbo({ ids }: { ids: ReactNode }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [budgetName, setBudgetName] = useMetadataPref('budgetName');
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const [tema] = useTheme();
  const [idioma] = useGlobalPref('language');
  const { soloLectura, puedeCompartir, aBordo } = useCompartir();
  const { hasPermission } = useAuth();
  const esAdmin = hasPermission(Permissions.ADMINISTRATOR);
  const conMonedas = useFeatureFlag('currency');
  const userData = useSelector(state => state.user.data);
  // Solo con una cuenta en el servidor (no en «solo este dispositivo»).
  const conCuenta = Boolean(userData?.userId) && !userData?.offline;
  const { trato } = useTrato();
  const { avisos } = useAvisos();
  const { config: nomina } = useConfigNomina();
  const plan = usePlan();
  // «Seguir con Rumbo» (hoja de periodo terminado) llega con la fila abierta.
  const location = useLocation();
  const [abierta, setAbierta] = useState<string | null>(
    () => (location.state as { abrir?: string } | null)?.abrir ?? null,
  );
  const usuario = useSelector(state => state.user.data);
  const navigate = useNavigate();

  const alternar = (id: string) =>
    setAbierta(actual => (actual === id ? null : id));

  const fila = (props: Omit<FilaProps, 'abierta' | 'onPress'>) => (
    <Fila
      key={props.id}
      {...props}
      abierta={abierta === props.id}
      onPress={() => alternar(props.id)}
    />
  );

  // «Automático (como el móvil)» → «Automático», como en la maqueta.
  const nombreTema = (
    getThemeOptions(t).find(([id]) => id === tema)?.[1] ?? ''
  ).split(' (')[0];
  const nombreIdioma = idioma ? nombreDeIdioma(idioma) : t('System default');

  const onCambiar = () => {
    solicitarListaDePresupuestos();
    void dispatch(closeBudget());
  };

  return (
    <View style={{ gap: 22, width: '100%' }} data-testid="ajustes-rumbo">
      <View
        data-testid="presupuesto-abierto"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          padding: 14,
          borderRadius: radio.tarjeta,
          background: `linear-gradient(135deg, ${suave(color.accent, 28)}, ${color.surface})`,
        }}
      >
        <SvgLogo width={44} height={44} aria-hidden="true" />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              ...texto.titulo,
              color: color.fg,
              overflowWrap: 'anywhere',
            }}
          >
            {budgetName}
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>Your budget</Trans>
          </Text>
        </View>
        <Button
          data-testid="presupuesto-cambiar"
          onPress={onCambiar}
          style={{ minHeight: TACTIL, borderRadius: radio.pildora }}
        >
          <Trans>Switch</Trans>
        </Button>
      </View>

      <Grupo clave="presupuesto" titulo={t('Budget')}>
        {!soloLectura &&
          fila({
            id: 'nombre',
            icono: 'pencil',
            tono: '#3b6fd8',
            titulo: t('Name'),
            valor: budgetName,
            contenido: (
              <CambiarNombre
                actual={budgetName ?? ''}
                onGuardar={nombre => {
                  setBudgetName(nombre);
                  setAbierta(null);
                }}
              />
            ),
          })}
        {!soloLectura &&
          fila({
            id: 'amarre',
            icono: cloudFileId ? 'nube' : 'movil',
            tono: cloudFileId ? '#2b7a8c' : '#5d6f96',
            titulo: t('Mooring'),
            valor: cloudFileId ? t('Phone and cloud') : t('Waiting to upload'),
            contenido: <AmarreSettings />,
          })}
        {!soloLectura &&
          fila({
            id: 'travesia',
            icono: 'compass',
            tono: '#0f7c7a',
            titulo: t('First voyage'),
            detalle: t('The tutorial with Gavi, whenever you want'),
            contenido: <TravesiaSettings />,
          })}
        {fila({
          id: 'trato',
          icono: 'user',
          tono: '#b0577a',
          titulo: t('How I talk to you'),
          valor: trato ? nombreTrato(t, trato) : t('Not chosen'),
          contenido: <TratoSettings />,
        })}
        {fila({
          id: 'notificaciones',
          icono: 'bell',
          tono: '#c47f1a',
          titulo: t('Notifications'),
          detalle: resumenAvisos(t, avisos),
          contenido: <NotificacionesSettings />,
        })}
        {!soloLectura &&
          fila({
            id: 'nomina',
            icono: 'coins',
            tono: '#1f8f5c',
            titulo: t('Your payroll'),
            valor: resumenNomina(t, nomina),
            contenido: <NominaSettings />,
          })}
        {(soloLectura || puedeCompartir || aBordo) &&
          fila({
            id: 'compartir',
            icono: 'users',
            tono: '#2e8b62',
            titulo: t('Share'),
            detalle: soloLectura
              ? t('Shared with you to view only')
              : puedeCompartir
                ? t('Who else can open this budget')
                : t('You are aboard this budget'),
            contenido: <CompartirSettings />,
          })}
        {conCuenta &&
          fila({
            id: 'plan',
            icono: 'velero',
            tono: '#1f6f8b',
            titulo: t('Your plan'),
            valor: plan.plan ? nombrePlan(t, plan.plan.plan) : undefined,
            contenido: <PlanSettings estado={plan} />,
          })}
        {esAdmin &&
          fila({
            id: 'banco',
            icono: 'bank',
            tono: '#8a63d2',
            titulo: t('Bank connection'),
            detalle: t('Only you see this, as administrator'),
            contenido: <EnableBankingSettings />,
          })}
        {esAdmin &&
          fila({
            id: 'puente',
            icono: 'compass',
            tono: '#12355b',
            titulo: t('Command bridge'),
            detalle: t('Who uses Rumbo, what fails, who to write to'),
            contenido: (
              <View style={{ gap: 12 }}>
                <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
                  <Trans>
                    Only you see this, as administrator. It never shows amounts,
                    accounts or anything people type.
                  </Trans>
                </Text>
                <Boton bloque onPress={() => void navigate('/puente')}>
                  <Trans>Open the command bridge</Trans>
                </Boton>
              </View>
            ),
          })}
      </Grupo>

      <Grupo clave="datos" titulo={t('Your data')}>
        {fila({
          id: 'copia',
          icono: 'refresh',
          tono: '#2e8b62',
          titulo: t('Backup'),
          detalle: t('A file with all your data'),
          contenido: <CopiaSettings />,
        })}
        {fila({
          id: 'exportar',
          icono: 'arrowdown',
          tono: '#d08a2e',
          titulo: t('Export data'),
          detalle: t('A .zip file with everything'),
          contenido: <ExportBudget />,
        })}
        {fila({
          id: 'uso',
          icono: 'pie',
          tono: '#4b5a78',
          titulo: t('Usage data'),
          detalle: t('To improve Rumbo'),
          contenido: <UsoSettings />,
        })}
        {conCuenta &&
          fila({
            id: 'encuesta',
            icono: 'sparkle',
            tono: '#b8892f',
            titulo: t('Beta survey'),
            detalle: t('Two minutes to tell me how Rumbo is going'),
            contenido: <EncuestaSettings />,
          })}
        {conCuenta &&
          fila({
            id: 'algo-no-va',
            icono: 'note',
            tono: '#c0603a',
            titulo: t('Something is wrong / Ask for a feature'),
            detalle: t('Write to the person who runs Rumbo'),
            contenido: <AlgoNoVaSettings />,
          })}
        {conCuenta &&
          fila({
            id: 'borrar-cuenta',
            icono: 'trash',
            tono: color.bad,
            titulo: t('Delete my account and my data'),
            detalle: t('From the server and from this device'),
            contenido: <BorrarCuentaSettings />,
          })}
      </Grupo>

      <Grupo clave="apariencia" titulo={t('Appearance')}>
        {fila({
          id: 'tema',
          icono: 'moon',
          tono: GRIS_PIZARRA,
          titulo: t('Theme'),
          valor: nombreTema,
          contenido: <ThemeSettings />,
        })}
        {fila({
          id: 'tamano',
          icono: 'type',
          tono: GRIS_PIZARRA,
          titulo: t('Size'),
          valor: nombreEscala(t, escalaElegida()).split(' (')[0],
          contenido: <EscalaSettings />,
        })}
        {fila({
          id: 'formato',
          icono: 'coins',
          tono: GRIS_PIZARRA,
          titulo: t('Format'),
          detalle: t('Numbers, dates and first day of the week'),
          contenido: (
            <View style={{ gap: 12 }}>
              <FormatSettings />
              {conMonedas && <CurrencySettings />}
            </View>
          ),
        })}
        {fila({
          id: 'idioma',
          icono: 'info',
          tono: GRIS_PIZARRA,
          titulo: t('Language'),
          valor: nombreIdioma,
          contenido: <LanguageSettings />,
        })}
      </Grupo>

      {usuario && !usuario.offline && (
        <Grupo clave="cuenta" titulo={t('Account')}>
          {puedeUsarHuella() &&
            fila({
              id: 'huella',
              icono: 'lock',
              tono: '#2f7d6d',
              titulo: t('Fingerprint'),
              detalle: t('Sign in without typing the password'),
              contenido: <AjustesHuella />,
            })}
          {fila({
            id: 'repuesto',
            icono: 'lock',
            tono: '#8a6d2f',
            titulo: t('Spare key'),
            detalle: t('Create a new one if you lost it'),
            contenido: <NuevaLlaveRepuesto />,
          })}
          {fila({
            id: 'cuenta',
            icono: 'user',
            tono: '#2f7d6d',
            titulo: t('Sign out'),
            detalle: usuario.userName ?? undefined,
            contenido: (
              <View style={{ gap: 12 }}>
                <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
                  <Trans>
                    You sign out on this device. Your data stays safe in the
                    cloud; to come back you&apos;ll need your account and your
                    Rumbo password.
                  </Trans>
                </Text>
                <Boton
                  variante="peligro"
                  bloque
                  onPress={() => void dispatch(signOut())}
                >
                  <Trans>Sign out</Trans>
                </Boton>
              </View>
            ),
          })}
        </Grupo>
      )}

      <Grupo clave="avanzado" titulo={t('Advanced')}>
        {fila({
          id: 'herramientas',
          icono: 'settings',
          tono: '#4b5a78',
          titulo: t('Tools'),
          detalle: t('IDs, reset cache, repair transactions'),
          contenido: (
            <View style={{ gap: 12 }}>
              {ids}
              <ResetCache />
              <RepairTransactions />
            </View>
          ),
        })}
      </Grupo>

      <PieVersion />
    </View>
  );
}

function Grupo({
  clave,
  titulo,
  children,
}: {
  /** Para que la ayuda de Gavi señale el grupo entero. */
  clave: string;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 8 }} data-testid={`grupo-${clave}`}>
      <Text
        role="heading"
        aria-level={2}
        style={{ ...texto.etiqueta, color: color.fg3, paddingLeft: 4 }}
      >
        {titulo}
      </Text>
      <View
        style={{
          backgroundColor: color.surface,
          borderRadius: radio.tarjeta,
          overflow: 'hidden',
        }}
      >
        {children}
      </View>
    </View>
  );
}

type FilaProps = {
  id: string;
  icono: NombreIcono;
  tono: string;
  titulo: string;
  detalle?: string;
  /** Valor actual, a la derecha (p. ej. «Oscuro»). */
  valor?: string | null;
  contenido: ReactNode;
  abierta: boolean;
  onPress: () => void;
};

function Fila({
  id,
  icono,
  tono,
  titulo,
  detalle,
  valor,
  contenido,
  abierta,
  onPress,
}: FilaProps) {
  return (
    <View
      className={css({ '& + &': { borderTop: `1px solid ${color.line}` } })}
    >
      <Button
        variant="bare"
        data-testid={`ajuste-${id}`}
        aria-expanded={abierta}
        onPress={onPress}
        style={{
          minHeight: Math.max(TACTIL, 56),
          justifyContent: 'flex-start',
          gap: 12,
          padding: '8px 14px',
          borderRadius: 0,
          textAlign: 'left',
          color: color.fg,
        }}
      >
        <View
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: radio.sm - 2,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: tono,
            color: '#fff',
          }}
        >
          <Icono nombre={icono} size={17} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ ...texto.fila, fontSize: 14.5, color: color.fg }}>
            {titulo}
          </Text>
          {detalle && (
            <Text
              style={{
                ...texto.secundario,
                fontSize: 12,
                color: color.fg2,
              }}
            >
              {detalle}
            </Text>
          )}
        </View>
        {valor && (
          <Text
            style={{
              ...texto.secundario,
              color: color.fg2,
              maxWidth: '40%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {valor}
          </Text>
        )}
        <Icono
          nombre="cr"
          size={18}
          style={{
            color: color.fg3,
            transform: abierta ? 'rotate(90deg)' : 'none',
            transition: `transform ${movimiento.pildora}ms ${movimiento.suave}`,
          }}
        />
      </Button>
      {abierta && (
        <View
          data-testid={`ajuste-${id}-contenido`}
          className={css({
            padding: '0 12px 14px',
            animation: `${despliega} ${movimiento.tarjeta}ms ${movimiento.suave}`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        >
          {contenido}
        </View>
      )}
    </View>
  );
}

/** Pizarra de los iconos de Apariencia (se lee en claro y en oscuro). */
const GRIS_PIZARRA = '#4a5878';

function nombreDeIdioma(codigo: string): string {
  if (codigo in NOMBRES_IDIOMAS) {
    return NOMBRES_IDIOMAS[codigo];
  }
  try {
    const nombre = new Intl.DisplayNames([codigo], { type: 'language' }).of(
      codigo,
    );
    return nombre ? nombre.charAt(0).toUpperCase() + nombre.slice(1) : codigo;
  } catch {
    return codigo;
  }
}

function CambiarNombre({
  actual,
  onGuardar,
}: {
  actual: string;
  onGuardar: (nombre: string) => void;
}) {
  const { t } = useTranslation();
  const [nombre, setNombre] = useState(actual);
  const limpio = nombre.trim();
  const guardar = () => {
    if (limpio && limpio !== actual) {
      onGuardar(limpio);
    }
  };
  return (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      <Input
        aria-label={t('Budget name')}
        data-testid="ajuste-nombre-campo"
        value={nombre}
        onChangeValue={setNombre}
        onEnter={guardar}
        style={{ flex: 1, minHeight: TACTIL, fontSize: 16 }}
      />
      <Button
        variant="primary"
        isDisabled={!limpio || limpio === actual}
        onPress={guardar}
        style={{ minHeight: TACTIL, borderRadius: radio.pildora }}
      >
        <Trans>Save</Trans>
      </Button>
    </View>
  );
}

function PieVersion() {
  const { t } = useTranslation();
  const { version, commit } = versionRumbo(
    import.meta.env.REACT_APP_VERSION_RUMBO,
    import.meta.env.REACT_APP_COMMIT,
  );
  return (
    <View
      style={{ alignItems: 'center', gap: 10, paddingTop: 4 }}
      data-vrt-mask
    >
      <SvgLogo width={40} height={40} aria-hidden="true" />
      <View style={{ alignItems: 'center', gap: 2 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Text style={{ ...texto.titulo, color: color.fg }}>
            {version
              ? t('Rumbo {{version}}', { version })
              : t('Rumbo, test version')}
          </Text>
          <BotonLicencias />
        </View>
        {commit && (
          <Text style={{ ...texto.secundario, fontSize: 12, color: color.fg3 }}>
            {commit}
          </Text>
        )}
      </View>
      <Actualizaciones conVersion={false} />
      {/* Textos legales; las licencias (aviso MIT exigido) van en Condiciones. */}
      <Text style={{ ...texto.secundario, fontSize: 12, color: color.fg3 }}>
        <Trans
          i18nKey="<privacy>Privacy</privacy> · <terms>Terms and licenses</terms>"
          components={{
            privacy: (
              <Link variant="external" linkColor="muted" to="/privacidad/" />
            ),
            terms: (
              <Link
                variant="external"
                linkColor="muted"
                to="/condiciones/#licencias"
              />
            ),
          }}
        />
      </Text>
    </View>
  );
}

const despliega = keyframes({
  from: { opacity: 0, transform: 'translateY(-6px)' },
  to: { opacity: 1, transform: 'none' },
});
